import { useState } from "react";
import { Save, XCircle, Building2 } from "lucide-react";
import SocioSearch from "../../../../common/SocioSearch";
import { Banner } from "../../../../common/Banner";
import SocioInfoCard  from "./SocioInfoCard";
import PeriodicityConfig from "./PeriodicityConfig";
import QuotasList from "./QuotaList";
import Swal from "sweetalert2";
import { getActiveQuotas, getQuotas, updatePeriodicityAndQuotas } from "../../../../../services/quotasService";
import { periodicities } from "../../../../../constants/options"
import { ActionLoader, PageLoader } from "../../../../loader";


// Componente principal
const Periodicity = () => {
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [currentQuotas, setCurrentQuotas] = useState([]);
  const [newQuotas, setNewQuotas] = useState([]);
  const [newPeriodicity, setNewPeriodicity] = useState("Mensual");
  const [newAmount, setNewAmount] = useState("");
  const [isLoadingQuotas, setIsLoadingQuotas] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCalculatedNewQuotas, setHasCalculatedNewQuotas] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  
  const handleSocioSelect = async (socio) => {
    setSelectedSocio(socio);
    const availableOptions = periodicities.filter(option => option.value !== socio.periodicity);
    setNewPeriodicity(availableOptions.length > 0 ? availableOptions[0].value : "Mensual");
    setNewAmount(socio.amount?.toString() || "0");
    setHasCalculatedNewQuotas(false);
    setNewQuotas([]);
    setIsLoadingQuotas(true);
    setIsDisabled(false); // Resetear el estado disabled
    
    try {
      const response = await getActiveQuotas(socio.rut, new Date().getFullYear());
      setCurrentQuotas(response);
      
      // Verificar usando la response directamente, no el estado
      if (response.error || !response || response.length === 0 || response.message) {
        const alertText = response.message || 'No se encontraron cuotas activas para este socio';
        Swal.fire({
          title: 'Atención',
          text: alertText,
          icon: 'info',
          confirmButtonText: 'Aceptar'
        });
        setIsDisabled(true);
      }
    } catch (error) {
      console.error("Error fetching quotas:", error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las cuotas sociales',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      setIsDisabled(true); // También deshabilitar en caso de error
    } finally {
      setIsLoadingQuotas(false);
    }
  };

  const calculateNewQuotas = () => {
    if (!newPeriodicity || !newAmount) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor, complete la periodicidad y el monto',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    setIsCalculating(true);
    
    // Simular cálculo
    setTimeout(() => {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      let generatedQuotas = [];
      
      // Generar cuotas según la periodicidad hasta el mes actual
      if (newPeriodicity === "Mensual") {
        for (let month = 1; month <= currentMonth; month++) {
          generatedQuotas.push({
            id: `new-${month}`,
            amount: parseFloat(newAmount) * 35000, // Mock UF value
            period: getMonthName(month),
            year: currentYear,
            status: "pending",
            uf_value: 35000,
            payment_date: null
          });
        }
      } else if (newPeriodicity === "Semestral") {
        const semester = Math.ceil(currentMonth / 6);
        for (let s = 1; s <= semester; s++) {
          generatedQuotas.push({
            id: `new-s${s}`,
            amount: parseFloat(newAmount) * 35000 * 6,
            period: `Semestre ${s}`,
            year: currentYear,
            status: "pending",
            uf_value: 35000,
            payment_date: null
          });
        }
      } else if (newPeriodicity === "Anual") {
        generatedQuotas.push({
          id: `new-annual`,
          amount: parseFloat(newAmount) * 35000 * 12,
          period: "Anual",
          year: currentYear,
          status: "pending",
          uf_value: 35000,
          payment_date: null
        });
      }
      
      setNewQuotas(generatedQuotas);
      setHasCalculatedNewQuotas(true);
      setIsCalculating(false);
    }, 1500);
  };

  const getMonthName = (month) => {
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    return months[month - 1];
  };

  const handleNewQuotaChange = (index, updatedQuota) => {
    const updatedQuotas = [...newQuotas];
    updatedQuotas[index] = updatedQuota;
    setNewQuotas(updatedQuotas);
  };

  const handleSaveChanges = async () => {
    try {
      const result = await Swal.fire({
        title: '¿Confirmar cambios?',
        text: 'Esta acción actualizará la periodicidad y generará las nuevas cuotas sociales.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar cambios',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        setIsSubmitting(true);
        const cleanedQuotas = newQuotas.map(({ id, ...rest }) => ({
          ...rest
        }));

        const response = await updatePeriodicityAndQuotas(
          selectedSocio.rut,
          new Date().getFullYear(),
          newPeriodicity,
          parseFloat(newAmount),
          cleanedQuotas
        );
        if (response.error) {
          Swal.fire({
            title: 'Error',
            text: response.message,
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }

        Swal.fire({
          title: 'Éxito',
          text: 'Los cambios se han guardado correctamente.',
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });
        //limpiar todo
        setSelectedSocio(null);
        setHasCalculatedNewQuotas(false);
        setCurrentQuotas([]);
        setNewQuotas([]);
        setIsDisabled(false); // Resetear también el estado disabled
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudieron guardar los cambios',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <Banner title="Administrador de Periodicidad" message="Gestiona la periodicidad de los pagos de las empresas y sus cuotas sociales." />
        
        {/* Búsqueda de Socio */}
        <div className="mb-6">
          <SocioSearch onSocioSelect={handleSocioSelect} />
        </div>

        {/* Información del Socio */}
        {selectedSocio && (
          <div className="mb-6">
            <SocioInfoCard selectedSocio={selectedSocio} />
          </div>
        )}

        {/* Configuración de Periodicidad */}
        {selectedSocio && (
          <div className="mb-6">
            <PeriodicityConfig
              currentPeriodicity={selectedSocio.periodicity}
              currentAmount={selectedSocio.amount}
              newPeriodicity={newPeriodicity}
              newAmount={newAmount}
              onPeriodicityChange={setNewPeriodicity}
              onAmountChange={setNewAmount}
              onCalculateNewQuotas={calculateNewQuotas}
              isCalculating={isCalculating}
              isDisabled={isDisabled}
            />
          </div>
        )}

        {/* Comparación de Cuotas */}
        {selectedSocio && (
          <div className="flex flex-col gap-6 mb-6">
            {isLoadingQuotas ? (
              <PageLoader message="Cargando cuotas del socio..." />
            ) : (
              <>
                {currentQuotas && currentQuotas.length > 0 ? (
                  <QuotasList
                    title="Cuotas Actuales"
                    quotas={currentQuotas}
                    isEditable={false}
                    emptyMessage="No hay cuotas registradas"
                  />
                ) : (
                  <div className="p-4 text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-xl">
                    La compañía no tiene cuotas creadas.
                  </div>
                )}

                <QuotasList
                  title="Nuevas Cuotas (Editables)"
                  quotas={newQuotas}
                  isEditable={true}
                  onQuotaChange={handleNewQuotaChange}
                  emptyMessage="Calcule las nuevas cuotas para visualizarlas aquí"
                />
              </>
            )}
          </div>
        )}
        {/* Botones de Acción */}
        {hasCalculatedNewQuotas && (
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                setHasCalculatedNewQuotas(false);
                setNewQuotas([]);
              }}
              className="flex items-center gap-2 px-6 py-2 bg-[#DC3465] text-white rounded-lg hover:bg-[#c12d5a] transition-colors"
            >
              <XCircle size={16} />
              Cancelar
            </button>
            <button
              onClick={handleSaveChanges}
              className="flex items-center gap-2 px-6 py-2 bg-[#1BBB55] text-white rounded-lg hover:bg-[#16a049] transition-colors"
            >
              <Save size={16} />
              Guardar Cambios
            </button>
          </div>
        )}

        {/* Estado inicial */}
        {!selectedSocio && (
          <div className="text-center py-12 text-gray-500">
            <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Busca y selecciona una empresa para administrar su periodicidad</p>
          </div>
        )}
      </div>
      <ActionLoader open={isSubmitting} onClose={() => setIsSubmitting(false)} />
    </div>
  );
};

export default Periodicity;